/**
 * Modale « Notifications » : liste paginée des notifications de l'utilisateur (les invalidées ne
 * sont jamais montrées). Une ligne = pastille du sujet, titre (gras tant que non lu), temps relatif
 * FR. Le clic marque la ligne comme lue, navigue vers `linkPath` et ferme la modale.
 */
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { FiCheckCircle, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { Modal } from "./Modal";
import { formatExactDateFr, formatRelativeTimeFr } from "../Helper/dates";
import { useNotifications } from "../Realtime/useNotifications";
import { notificationsClient, type AdminNotification } from "../Pages/Notifications/notificationsClient";

/** Libellé FR + couleurs de la pastille pour chaque sujet connu ; repli neutre sur le sujet brut. */
export const topicPill = (topic: string): { label: string; className: string } => {
	switch (topic) {
		case "contact-messages":
			return { label: "Messages de contact", className: "bg-sky-100 text-sky-700" };
		case "backup-errors":
			return { label: "Erreurs de sauvegarde", className: "bg-rose-100 text-rose-700" };
		case "redirect-tasks":
			return { label: "Redirections à corriger", className: "bg-amber-100 text-amber-700" };
		case "tickets":
			return { label: "Tickets", className: "bg-violet-100 text-violet-700" };
		default:
			return { label: topic || "Autre", className: "bg-slate-100 text-slate-600" };
	}
};

export interface NotificationsModalProps {
	open: boolean;
	onClose: () => void;
}

const NotificationRow: React.FC<{ notification: AdminNotification; onOpen: (n: AdminNotification) => void }> = ({
	notification,
	onOpen
}) => {
	const pill = topicPill(notification.topic);
	const unread = notification.status === "unread";
	return (
		<li>
			<button
				type="button"
				onClick={() => onOpen(notification)}
				className="flex w-full items-start gap-3 px-5 py-3 text-left transition hover:bg-slate-50"
			>
				<span className={`mt-0.5 inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${pill.className}`}>
					{pill.label}
				</span>
				<span className="min-w-0 flex-1">
					<span className={`block truncate text-sm ${unread ? "font-bold text-slate-900" : "font-medium text-slate-600"}`}>
						{unread && <span className="mr-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-[#005CA9] align-middle" aria-label="Non lu" />}
						{notification.title}
					</span>
					{notification.body && <span className="mt-0.5 block truncate text-xs text-slate-500">{notification.body}</span>}
				</span>
				<span className="shrink-0 text-[11px] text-slate-400" title={formatExactDateFr(notification.createdAt)}>
					{formatRelativeTimeFr(notification.createdAt)}
				</span>
			</button>
		</li>
	);
};

export const NotificationsModal: React.FC<NotificationsModalProps> = ({ open, onClose }) => {
	const history = useHistory();
	const { items, loading, page, hasMore, setPage, reload, markRead } = useNotifications(open);
	const [markingRedirects, setMarkingRedirects] = useState(false);

	// Le sujet historiquement inondant : offrir un « tout lu » ciblé quand des non-lues
	// de redirections sont affichées (le préfixe couvre clefs par URL et clefs par id).
	const hasUnreadRedirects = items.some((n) => n.topic === "redirect-tasks" && n.status === "unread");

	const markRedirectsRead = async () => {
		setMarkingRedirects(true);
		try {
			await notificationsClient.markReadBySource("redirect-task:");
			await reload();
		} catch (err) {
			console.error("Échec du marquage des notifications de redirections", err);
		} finally {
			setMarkingRedirects(false);
		}
	};

	const openNotification = async (notification: AdminNotification) => {
		if (notification.status === "unread") {
			await markRead(notification.id);
		}
		if (notification.linkPath) {
			history.push(notification.linkPath);
		}
		onClose();
	};

	return (
		<Modal show={open} onHide={onClose} size="lg" centered>
			<Modal.Header closeButton>
				<Modal.Title>Notifications</Modal.Title>
			</Modal.Header>
			<Modal.Body className="p-0" scrollable>
				{loading ? (
					<ul className="divide-y divide-slate-100">
						{Array.from({ length: 6 }).map((_, i) => (
							<li key={`notif-skel-${i}`} className="flex items-center gap-3 px-5 py-3">
								<span className="h-5 w-24 rounded-full bg-slate-100" />
								<div className="h-3 w-2/3 rounded bg-slate-100" />
							</li>
						))}
					</ul>
				) : items.length === 0 ? (
					<div className="px-5 py-16 text-center text-sm text-slate-500">Aucune notification.</div>
				) : (
					<ul className="divide-y divide-slate-100">
						{items.map((n) => (
							<NotificationRow key={n.id} notification={n} onOpen={openNotification} />
						))}
					</ul>
				)}
			</Modal.Body>
			<Modal.Footer className="justify-between">
				<div className="flex items-center gap-3">
					<span className="text-xs text-slate-500">Page {page}</span>
					{hasUnreadRedirects && (
						<button
							type="button"
							onClick={markRedirectsRead}
							disabled={markingRedirects}
							className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 transition hover:bg-amber-100 disabled:opacity-50"
							title="Marque comme lues toutes vos notifications de redirections"
						>
							<FiCheckCircle className="h-3.5 w-3.5" />
							{markingRedirects ? "Marquage…" : "Marquer lues : redirections"}
						</button>
					)}
				</div>
				<div className="flex items-center gap-2">
					<button
						type="button"
						onClick={() => setPage(Math.max(1, page - 1))}
						disabled={loading || page <= 1}
						className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
					>
						<FiChevronLeft className="h-3.5 w-3.5" /> Précédent
					</button>
					<button
						type="button"
						onClick={() => setPage(page + 1)}
						disabled={loading || !hasMore}
						className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
					>
						Suivant <FiChevronRight className="h-3.5 w-3.5" />
					</button>
				</div>
			</Modal.Footer>
		</Modal>
	);
};

export default NotificationsModal;
